import React, { useState } from "react";
import { RouteComponentProps, useHistory } from "react-router";
import { PageHeader } from "../../../components/layout/header/Header";
import { StandardPageWrapper } from "../../../components/layout/StandardPageWrapper";
import { ProgressFooter, ProgressFooterActions } from "../../get-compliant/ProgressFooter";
import { PageLoading } from "../../../../common/components/Loading";
import { usePrimaryOrganizationId } from "../../../hooks";
import { useDpa, useDpaApprovalParties } from "../../../hooks/useDataProtectionAssessment";
import { Button } from "@mui/material";
import { RouteHelpers, Routes } from "../../../root/routes";
import { Callout, CalloutVariant } from "../../../components/Callout";
import { Api } from "../../../../common/service/Api";
import { useActionRequest } from "../../../../common/hooks/api";
import { ConfirmDialog } from "../../../components/dialog";
import { ApprovalPartiesTable } from "./DPAAddApprovalParties";
import { ApiRouteHelpers } from "../../../../common/root/apiRoutes";
import { CheckCircle, Info } from "@mui/icons-material";
import { DPAProgressStepper } from "./DPAProgressStepper";
import { dpaApprovalPartyStatusToHumanReadable } from "../../../../common/service/server/dto/DataProtectionAssessmentDto";
import { Link } from "react-router-dom";

type MatchProps = {
  dpaId: string;
};
type Props = RouteComponentProps<MatchProps> & {
  add: boolean;
};

export const DPAGatherSignatures: React.FC<Props> = ({ match }) => {
  const history = useHistory();
  const dpaId = match.params.dpaId;
  const orgId = usePrimaryOrganizationId();
  const [dpa, , refreshDpa] = useDpa(orgId, dpaId);
  const [approvalParties, ,] = useDpaApprovalParties(orgId, dpaId);

  const [confirmRequestSignatureDialogOpen, setConfirmRequestSignatureDialogOpen] = useState(false);
  const [confirmCancelDialogOpen, setConfirmCancelDialogOpen] = useState(false);

  const [showSignatureRequestSentToast, setShowSignatureRequestSentToast] = useState(false);
  const [showCanceledToast, setShowCanceledToast] = useState(false);

  const hideAllToasts = () => {
    setShowSignatureRequestSentToast(false);
    setShowCanceledToast(false);
  };

  const { fetch: finalizeSignatureAction } = useActionRequest({
    api: async () => {
      await Api.dataProtectionAssessment.finalizeSignatures(orgId, dpaId);
      await refreshDpa();
    },
  });

  const { fetch: cancelSignatureRequestAction } = useActionRequest({
    api: async () => {
      await Api.dataProtectionAssessment.cancelSignatureRequest(orgId, dpaId);
      await refreshDpa();
    },
  });

  if (!orgId || !dpa || !approvalParties) {
    return <PageLoading />;
  }

  return (
    <StandardPageWrapper
      footer={
        <ProgressFooter
          actions={
            <ProgressFooterActions
              prevContent={"Back"}
              prevOnClick={() => {
                history.push(
                  RouteHelpers.documents.dataProtectionAssessment.addApprovalParties(dpaId),
                );
              }}
              prevDisabled={dpa.status !== "WRITING"}
              nextContent={"Done"}
              nextLoading={false}
              nextOnClick={() => history.push(Routes.documents.dataProtectionAssessments)}
            />
          }
        />
      }
    >
      <Link to={Routes.documents.dataProtectionAssessments} className="text-decoration-none">
        <div
          className="text-t1 color-neutral-700"
          style={{ lineHeight: "21px", letterSpacing: "1.12px" }}
        >
          &lt; BACK TO MY ASSESSMENTS
        </div>
      </Link>
      <PageHeader titleContent="Data Protection Assessment" />
      <DPAProgressStepper activeStep={2} />
      {showSignatureRequestSentToast && (
        <Callout variant={CalloutVariant.Green} padding="0" className="mt-xl">
          <div className="d-flex flex--align-center">
            <div>
              <CheckCircle />
            </div>
            <div className="ml-md color-green-900 text-weight-bold text-t2 pb-xxxs">
              {dpa.name} has been sent to the requested parties for signatures.
            </div>
          </div>
        </Callout>
      )}
      {showCanceledToast && (
        <Callout variant={CalloutVariant.Yellow} padding="0" className="mt-xl">
          <div className="d-flex flex--align-center">
            <div>
              <Info />
            </div>
            <div className="ml-md color-yellow-900 text-weight-bold text-t2 pb-xxxs">
              {dpa.name} signature request has been canceled.
            </div>
          </div>
        </Callout>
      )}
      <div className="text-color-primary text-t5 text-weight-medium mt-xl">Review Document</div>
      <div className="text-t3 text-weight-medium">
        Review final assessment document before sending for signatures.
      </div>
      <div className="d-flex flex--center mt-xl">
        <Button
          variant="contained"
          size="large"
          color="info"
          onClick={() => {
            window.open(
              ApiRouteHelpers.organization.documents.dataProtectionAssessments.pdf(orgId, dpaId),
            );
          }}
        >
          Review Final DPA
        </Button>
      </div>
      {dpa.status === "REQUESTED_SIGNATURES" && (
        <div className="d-flex flex--center mt-md">
          <a
            className="text-t2 text-weight-medium color-red-500"
            href="#"
            onClick={async (e) => {
              e.preventDefault();
              setConfirmCancelDialogOpen(true);
            }}
          >
            Cancel Request
          </a>
          <ConfirmDialog
            open={confirmCancelDialogOpen}
            onClose={() => setConfirmCancelDialogOpen(false)}
            confirmText="Cancel Request"
            confirmTitle="This will cancel the signature request"
            contentText={`The approving parties will get notified that the request to sign has been canceled and you will be able to revise your assessment.`}
            onConfirm={async () => {
              await cancelSignatureRequestAction();
              await refreshDpa();

              hideAllToasts();
              setShowCanceledToast(true);
            }}
          />
        </div>
      )}
      <div className="text-color-primary text-t5 text-weight-medium mt-xl">Gather Signatures</div>
      <div className="text-t3 text-weight-medium">
        {dpa.name} will be sent for signatures to the parties below.
      </div>
      {dpa.status == "WRITING" && (
        <ApprovalPartiesTable
          editable={false}
          addButtonTitle=""
          approvalParties={approvalParties.filter((party) => party.purpose === "SIGNER")}
          partyType="EXTERNAL"
          purpose="SIGNER"
        />
      )}
      {dpa.status == "REQUESTED_SIGNATURES" && (
        <div className="mt-md">
          <div className="signatures-table-row">
            <div className="bg-color-primary-100 color-neutral-800">Name</div>
            <div className="bg-color-primary-100 color-neutral-800">Role</div>
            <div className="bg-color-primary-100 color-neutral-800">Email</div>
            <div className="bg-color-primary-100 color-neutral-800">Signature</div>
          </div>
          {approvalParties
            .filter((party) => party.purpose === "SIGNER")
            .map((party) => {
              return (
                <div className="signatures-table-row" key={party.id}>
                  <div>{party.name}</div>
                  <div>{party.title}</div>
                  <div>{party.email}</div>
                  <div>{dpaApprovalPartyStatusToHumanReadable(party.status)}</div>
                </div>
              );
            })}
        </div>
      )}
      {dpa.status == "WRITING" && (
        <>
          <div className="d-flex flex--center mt-lg">
            <Button
              size="large"
              variant="contained"
              color="primary"
              onClick={() => setConfirmRequestSignatureDialogOpen(true)}
            >
              Request Signatures
            </Button>
          </div>
          <div className="d-flex flex--center">
            <p className="text-t2 color-primary-700 text-center" style={{ maxWidth: "45ch" }}>
              Double-check your document before sending. If you need to make additional changes
              after sending, cancel your request and resubmit a new request for the revised version.
            </p>
          </div>
          <ConfirmDialog
            open={confirmRequestSignatureDialogOpen}
            onClose={() => setConfirmRequestSignatureDialogOpen(false)}
            confirmText="Confirm Request"
            confirmTitle="This will send the DPA for approval"
            contentText={`If you need to make revisions to the document after requesting signatures, you will need to cancel the request in order to revise.`}
            onConfirm={async () => {
              await finalizeSignatureAction();
              await refreshDpa();
              setConfirmRequestSignatureDialogOpen(false);

              hideAllToasts();
              setShowSignatureRequestSentToast(true);
            }}
          />
        </>
      )}
    </StandardPageWrapper>
  );
};
